import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AIRecommendations = ({ onApplyRecommendation }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const recommendations = [
    {
      id: 1,
      type: 'gap_analysis',
      title: 'Pay Gap Detected in Engineering',
      description: `Analysis shows a 12% gender pay gap in Senior Software Engineer band. Consider adjusting salary ranges or reviewing individual compensation.`,
      impact: 'High',
      confidence: 92,
      suggestedAction: 'Increase minimum salary by €5,000 to reduce gap',
      affectedEmployees: 24,
      estimatedCost: '€120,000 annually'
    },
    {
      id: 2,
      type: 'band_optimization',
      title: 'Overlapping Salary Bands',
      description: `Mid-level and Senior Marketing bands have overlapping ranges (€45K-€65K vs €55K-€75K). This may cause confusion and inequity.`,
      impact: 'Medium',
      confidence: 87,
      suggestedAction: 'Adjust Mid-level maximum to €60K',
      affectedEmployees: 18,
      estimatedCost: 'Cost neutral'
    },
    {
      id: 3,
      type: 'market_alignment',
      title: 'Below Market Rate - Data Science',
      description: `Data Science bands are 15% below market rate based on recent salary surveys. Risk of talent retention issues.`,
      impact: 'High',
      confidence: 94,
      suggestedAction: 'Increase entire band range by 15%',
      affectedEmployees: 12,
      estimatedCost: '€180,000 annually'
    },
    {
      id: 4,
      type: 'compliance',
      title: 'EU Directive Compliance Check',
      description: `Current band structure meets EU Pay Transparency requirements. No immediate action needed.`,
      impact: 'Low',
      confidence: 98,
      suggestedAction: 'Continue monitoring quarterly',
      affectedEmployees: 0,
      estimatedCost: 'No cost'
    }
  ];

  const getImpactColor = (impact) => {
    switch (impact?.toLowerCase()) {
      case 'high':
        return 'text-destructive bg-destructive/10';
      case 'medium':
        return 'text-warning bg-warning/10';
      case 'low':
        return 'text-success bg-success/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'gap_analysis':
        return 'TrendingDown';
      case 'band_optimization':
        return 'Settings';
      case 'market_alignment':
        return 'BarChart3';
      case 'compliance':
        return 'Shield';
      default:
        return 'Lightbulb';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg mb-6">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Brain" size={16} className="text-primary" />
            </div>
            <div>
              <h3 className="font-medium text-foreground">AI Recommendations</h3>
              <p className="text-sm text-muted-foreground">
                {recommendations?.length} insights based on your pay band data
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName={isExpanded ? 'ChevronUp' : 'ChevronDown'}
            iconPosition="right"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? 'Collapse' : 'View All'}
          </Button>
        </div>
      </div>
      {isExpanded && (
        <div className="p-4 space-y-4">
          {recommendations?.map((rec) => (
            <div key={rec?.id} className="border border-border rounded-lg p-4">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name={getTypeIcon(rec?.type)} size={20} className="text-muted-foreground" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-medium text-foreground">{rec?.title}</h4>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getImpactColor(rec?.impact)}`}>
                          {rec?.impact} Impact
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {rec?.confidence}% confidence
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-3">
                    {rec?.description}
                  </p>
                  
                  <div className="bg-muted/50 rounded-lg p-3 mb-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                      <div>
                        <span className="font-medium text-foreground">Suggested Action:</span>
                        <p className="text-muted-foreground">{rec?.suggestedAction}</p>
                      </div>
                      <div>
                        <span className="font-medium text-foreground">Affected Employees:</span>
                        <p className="text-muted-foreground">{rec?.affectedEmployees}</p>
                      </div>
                      <div>
                        <span className="font-medium text-foreground">Estimated Cost:</span>
                        <p className="text-muted-foreground">{rec?.estimatedCost}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="default"
                      size="sm"
                      iconName="Check"
                      iconPosition="left"
                      onClick={() => onApplyRecommendation(rec)}
                    >
                      Apply Recommendation
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="ExternalLink"
                      iconPosition="left"
                    >
                      View Details
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="X"
                      iconPosition="left"
                    >
                      Dismiss
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {!isExpanded && (
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {recommendations?.slice(0, 4)?.map((rec) => (
              <div key={rec?.id} className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
                <Icon name={getTypeIcon(rec?.type)} size={16} className="text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{rec?.title}</p>
                  <p className={`text-xs ${getImpactColor(rec?.impact)?.split(' ')?.[0]}`}>
                    {rec?.impact} Impact
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AIRecommendations;